import React, { ReactElement } from 'react';
import {
  ReportStatus,
  useUpdateReportStatusMutation,
  AllReportsListDocument,
} from '@/__generated__/graphql';
import { useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import { Alert } from '@/components/shared/Alert';

const BATCH_SIZE = 5;

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

const showSuccess = (message: string): ReactElement =>
  React.createElement(Alert, { severity: 'success', children: message });

const showWarning = (message: string): ReactElement =>
  React.createElement(Alert, { severity: 'warning', children: message });

const showError = (message: string): ReactElement =>
  React.createElement(Alert, { severity: 'error', children: message });

export const useBulkReportPublish = ({
  setLoading,
}: {
  setLoading: (loading: boolean) => void;
}) => {
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const [updateReportStatus] = useUpdateReportStatusMutation({
    refetchQueries: [AllReportsListDocument],
  });

  const handleBulkPublish = async (draftReportIds: string[]) => {
    if (draftReportIds.length === 0) return;
    if (draftReportIds.length > BATCH_SIZE) {
      setSnackbarMessage({
        shouldShow: true,
        content: React.createElement(Alert, {
          severity: 'info',
          children: `You can publish up to ${BATCH_SIZE} reports at a time.`,
        }),
      });
      return;
    }
    try {
      setLoading(true);
      const chunks = chunk(draftReportIds, BATCH_SIZE);
      let successCount = 0;
      let failCount = 0;

      for (const batchIds of chunks) {
        const results = await Promise.allSettled(
          batchIds.map((reportId) =>
            updateReportStatus({
              variables: {
                input: {
                  reportId,
                  status: ReportStatus.Published,
                },
              },
            })
          )
        );

        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value?.data?.updateReportStatus?.id) {
            successCount += 1;
          } else {
            failCount += 1;
          }
        });
      }

      const total = draftReportIds.length;
      if (failCount === 0) {
        setSnackbarMessage({
          shouldShow: true,
          content: showSuccess(
            `${successCount} report${successCount === 1 ? '' : 's'} published successfully.`
          ),
        });
      } else if (successCount === 0) {
        setSnackbarMessage({
          shouldShow: true,
          content: showError('Failed to publish reports. Please try again.'),
        });
      } else {
        setSnackbarMessage({
          shouldShow: true,
          content: showWarning(
            `${successCount} of ${total} report${
              total === 1 ? '' : 's'
            } published successfully. ${failCount} failed.`
          ),
        });
      }
    } catch {
      setSnackbarMessage({
        shouldShow: true,
        content: showError('Failed to publish reports. Please try again.'),
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    handleBulkPublish,
  };
};
